/**
 * useAttachmentDisplay Hook (Core)
 * 
 * Fetches and converts attachment data for display in messages.
 * Loads attachment metadata from API and formats it for UI components.
 * 
 * This hook belongs in core because:
 * - It handles data fetching (business logic)
 * - Makes API calls
 * - Platform-agnostic logic
 * - Part of the conversation/message domain
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { apiClient } from '../api/client';
import { ApiUrls } from '../api/urls';

export interface AttachmentData {
  id: string;
  name: string;
  type: string;
  size: number;
  createdAt: string;
  file_url?: string; // For downloading/previewing
  content?: string; // Base64 content for inline display (e.g., images)
}

export interface AttachedFileDisplay {
  id: string;
  name: string;
  type: string;
  isImage: boolean;
  preview?: string;
  file_url?: string;
}

interface UseAttachmentDisplayOptions {
  attachmentIds?: string[];
}

export const useAttachmentDisplay = ({ 
  attachmentIds = [],
}: UseAttachmentDisplayOptions = {}) => {
  const [attachments, setAttachments] = useState<AttachedFileDisplay[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track the last fetched IDs to prevent duplicate fetches
  const lastFetchedIdsRef = useRef<string>('');
   
  // Create a stable string key for the IDs array
  const idsKey = attachmentIds.join(',');

  // Fetch attachment details from API
  useEffect(() => {
    if (attachmentIds.length === 0) {
      setAttachments([]);
      lastFetchedIdsRef.current = '';
      return;
    }

    // Skip if we already fetched these exact IDs
    if (lastFetchedIdsRef.current === idsKey) {
      return;
    }

    // Mark that we're about to fetch these IDs (prevents React Strict Mode double-mount)
    lastFetchedIdsRef.current = idsKey;
    
    setIsLoading(true);
    setError(null);

    const fetchAttachments = async () => {
      try {
        // Fetch attachment details for each ID
        const attachmentPromises = attachmentIds.map(async (id) => {
          const response = await apiClient.get(`${ApiUrls.chatAttachments}${id}`);
          return response.data as AttachmentData;
        });

        const attachmentData = await Promise.all(attachmentPromises);
        
        // Convert inline without callback to avoid dependency issues
        const displayAttachments = attachmentData.map((attachmentData) => ({
          id: attachmentData.id,
          name: attachmentData.name,
          type: attachmentData.type,
          isImage: attachmentData.type.startsWith('image/') || 
               ["image/png","image/jpg","image/jpeg","image/gif","image/bmp","image/webp","image/svg+xml", 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(attachmentData.type),
          preview: attachmentData.file_url || attachmentData.content,
          file_url: attachmentData.file_url,
        }));
        
        setAttachments(displayAttachments);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching attachments:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch attachments');
        setAttachments([]);
        setIsLoading(false);
      }
    };

    fetchAttachments();
  }, [idsKey]); // Only depend on idsKey, not the array itself

  return {
    attachments,
    isLoading,
    error,
  };
};
